/**
 * Generate Questions Page - 4-Step Workflow
 * Matches old frontend UI exactly: Upload → Extract → Configure → Generate
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { questionsService } from '@/services/questions.service';
import { contentService, ContentDocument } from '@/services/content.service';
import { examsService } from '@/services/exams.service';
import { coursesService } from '@/services/courses.service';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/authStore';
import type { MCQFormatType } from '@/components/MCQFormatSelector';
import {
  Upload,
  FileText,
  CheckCircle,
  TrendingUp,
  Clock,
  DollarSign,
  ArrowLeft,
  Loader,
  Sparkles,
  Search,
  Trash2,
  Edit2
} from 'lucide-react';

const QUESTION_TYPES = [
  {
    value: 'multiple_choice',
    label: 'Type 1: MCQ Contextualized',
    description: 'ANBIMA Type 1: ~800 chars (without spaces), 4 alternatives (~500 chars total)'
  },
  {
    value: 'short_answer',
    label: 'Type 2: Short Answer',
    description: 'ANBIMA Type 2: ~1,400 chars (without spaces), always associated with a case'
  },
  {
    value: 'case_study',
    label: 'Type 3: Case (Case Study)',
    description: 'ANBIMA Type 3: ~1,200 chars context, up to 3 linked questions'
  },
  {
    value: 'interactive',
    label: 'Type 4: Interactive (Chat)',
    description: 'ANBIMA Type 4: ~400 chars initial context, ~200 chars per prompt'
  },
];

const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', color: '#4caf50' },
  { value: 'medium', label: 'Medium', color: '#ff9800' },
  { value: 'hard', label: 'Hard', color: '#f44336' },
];

// ANBIMA Cognitive Domains (2026)
const COGNITIVE_DOMAINS = [
  { value: 1, label: 'Level 1', description: 'Knowledge/Recall' },
  { value: 2, label: 'Level 2', description: 'Comprehension' },
  { value: 3, label: 'Level 3', description: 'Application' },
  { value: 4, label: 'Level 4', description: 'Analysis/Synthesis' },
];

// ANBIMA Behavioral Skills (2026)
const BEHAVIORAL_SKILLS = [
  { value: 'context_analysis', label: 'Context Analysis' },
  { value: 'client_service', label: 'Client Service' },
  { value: 'communication', label: 'Communication' },
  { value: 'persuasion', label: 'Persuasion' },
  { value: 'professional_ethics', label: 'Professional Ethics' },
  { value: 'relationship_management', label: 'Relationship Management' },
];

// Command Verbs for Type 2 (Short Answer)
const COMMAND_VERBS = [
  { value: 'auto', label: 'Automatic (LLM decides)' },
  { value: 'cite', label: 'Cite' },
  { value: 'explique', label: 'Explain' },
  { value: 'compare', label: 'Compare' },
  { value: 'analise', label: 'Analyze' },
];

const STEPS = ['Upload Document', 'Extract Content', 'Configure Settings', 'Generate Questions'];

export function GenerateQuestions() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { accessToken } = useAuthStore();

  // Workflow state
  const [activeStep, setActiveStep] = useState(0);

  // File upload states
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Existing documents
  const [existingDocuments, setExistingDocuments] = useState<ContentDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  // Extraction states
  const [extractedOECodes, setExtractedOECodes] = useState<string[]>([]);
  const [extractedTopics, setExtractedTopics] = useState<Array<{ name: string; relatedOECodes: string[] }>>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [oeCodeCategories, setOeCodeCategories] = useState<Record<string, 'anbima' | 'non-anbima' | null>>({});

  // Generation states - SUPPORT: Single or Multiple OE codes
  const [formData, setFormData] = useState({
    exam: '',
    course: '',
    selectedOECode: '' as string,
    selectedOECodes: [] as string[], // Multiple OE codes support
    percentage: 50,
    questionTypes: ['multiple_choice'],
    questionTypeDistribution: {} as Record<string, number>,
    difficulty: 'medium',
    mcqFormat: 'anbima' as MCQFormatType,

    // Content Selection (Macrotema → Tema → Subtema)
    macrotema: '',
    tema: '',
    subtema: '',

    // Skills & Cognitive Domain (ANBIMA 2026)
    cognitiveDomain: undefined as number | undefined,
    behavioralSkills: [] as string[],

    // Question Type Sub-Options
    questionsPerCase: 2, // Type 3: 1-3 questions per case
    promptsPerInteractive: 5, // Type 4: 3-10 prompts per interactive

    // Command Verb (Type 2 only)
    commandVerb: 'auto' as string,

    // Validation Rules (toggleable)
    validationRules: {
      prohibitedPhrases: true,
      characterLimits: true,
      structuralValidation: true,
      caseAssociation: true,
    }
  });

  // Fetch exams
  const { data: exams = [] } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams({ status: 'active' });
      } catch (error) {
        console.error('Failed to fetch exams:', error);
        return [];
      }
    },
  });

  // Fetch all courses
  const { data: allCourses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        return await coursesService.getCourses();
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        return [];
      }
    },
  });

  // Filter courses based on selected exam
  const filteredCourses = allCourses.filter(course =>
    course.linked_exams.includes(formData.exam)
  );

  // Custom prompt mode
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  const MAX_QUESTIONS = 20;

  const calculateQuestionCount = (percentage: number): number => {
    return Math.max(1, Math.round((percentage / 100) * MAX_QUESTIONS));
  };

  // Helper: Calculate total distribution percentage
  const calculateTotalDistribution = (): number => {
    return Object.values(formData.questionTypeDistribution).reduce((sum, val) => sum + val, 0);
  };

  // Helper: Handle question type toggle
  const handleQuestionTypeToggle = (type: string, checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        questionTypes: [...formData.questionTypes, type],
        questionTypeDistribution: {
          ...formData.questionTypeDistribution,
          [type]: formData.questionTypes.length === 0 ? 100 : 0
        }
      });
    } else {
      const newTypes = formData.questionTypes.filter(t => t !== type);
      const newDist = { ...formData.questionTypeDistribution };
      delete newDist[type];
      setFormData({
        ...formData,
        questionTypes: newTypes,
        questionTypeDistribution: newDist
      });
    }
  };

  // Helper: Handle distribution change
  const handleDistributionChange = (type: string, value: number) => {
    setFormData({
      ...formData,
      questionTypeDistribution: {
        ...formData.questionTypeDistribution,
        [type]: value
      }
    });
  };

  // Helper: Handle behavioral skill toggle
  const handleBehavioralSkillToggle = (skill: string) => {
    if (formData.behavioralSkills.includes(skill)) {
      setFormData({
        ...formData,
        behavioralSkills: formData.behavioralSkills.filter(s => s !== skill)
      });
    } else {
      setFormData({
        ...formData,
        behavioralSkills: [...formData.behavioralSkills, skill]
      });
    }
  };

  // Fetch exams
  useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      const response = await examsService.getExams();
      return response || [];
    },
  });

  // Load existing documents
  useEffect(() => {
    const loadDocuments = async () => {
      if (!accessToken) {
        setLoadingDocuments(false);
        return;
      }

      try {
        const response = await contentService.getDocuments(
          { processingStatus: 'completed' },
          accessToken
        );
        setExistingDocuments(response.documents);
      } catch (err) {
        console.error('Failed to load existing documents:', err);
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [accessToken]);

  // File handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadedFile || !accessToken) {
      showToast('error', 'Upload Failed', 'Please select a file and ensure you are logged in');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 80));
      }, 200);

      const metadata = {
        title: uploadedFile.name.replace(/\.[^/.]+$/, ''),
        exam: formData.exam || 'CPro-R',
        category: 'study-guide' as const,
        version: new Date().getFullYear().toString(),
        language: 'pt-BR',
        publisher: 'ANBIMA',
        publishedDate: new Date().toISOString().split('T')[0],
        description: `Uploaded for question generation`
      };

      const response = await contentService.uploadDocument(uploadedFile, metadata, accessToken);

      clearInterval(progressInterval);
      setUploadProgress(100);

      showToast('success', 'Upload Complete', 'File uploaded successfully. Extracting topics...');

      setActiveStep(1);
      setTimeout(() => {
        handleExtraction(response.documentId);
      }, 500);
    } catch (err: any) {
      showToast('error', 'Upload Failed', err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleExtraction = async (documentId: string) => {
    if (!accessToken) return;

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const topicsResponse = await contentService.getDocumentTopics(documentId, accessToken);

      // Store topics with their OE codes - ensure relatedOECodes is preserved
      const topicsWithOECodes = (topicsResponse.topics || []).map(topic => ({
        name: topic.title || '', // Use 'title' from Topic interface
        relatedOECodes: topic.relatedOECodes || [] // Keep the OE codes with each topic
      })).filter(t => t.name); // Filter out topics without names

      setExtractedTopics(topicsWithOECodes);

      // Extract OE codes from relatedOECodes arrays and flatten
      const oeCodes = topicsWithOECodes
        .flatMap(topic => topic.relatedOECodes || [])
        .filter((code, index, self) => code && self.indexOf(code) === index)
        .sort();

      console.log('[Extraction] Topics:', topicsWithOECodes, 'OE Codes:', oeCodes);

      if (oeCodes.length === 0) {
        showToast('warning', 'No Topics Found', 'No OE codes were extracted. Questions will be marked as N/A.');
        const defaultCodes = ['N/A'];
        setExtractedOECodes(defaultCodes);
        setExtractedTopics([]);
        setSelectedTopics([]);
        setFormData(prev => ({ ...prev, selectedOECode: 'N/A', selectedOECodes: ['N/A'] }));
      } else {
        setExtractedOECodes(oeCodes);
        // Default: Select first OE code, but allow multi-select
        setFormData(prev => ({ ...prev, selectedOECode: oeCodes[0], selectedOECodes: [oeCodes[0]] }));
        showToast('success', 'Topics Extracted', `Found ${topicsResponse.topics.length} topics with ${oeCodes.length} OE codes. Select topics or use "Select All Topics".`);
      }

      setActiveStep(2);
    } catch (err: any) {
      console.error('Topic extraction error:', err);
      showToast('error', 'Extraction Failed', 'Failed to extract topics. Questions will be marked as N/A.');
      const defaultCodes = ['N/A'];
      setExtractedOECodes(defaultCodes);
      setFormData(prev => ({ ...prev, selectedOECode: 'N/A', selectedOECodes: ['N/A'] }));
      setActiveStep(2);
    }
  };

  const handleUseExisting = async (doc: ContentDocument) => {
    if (!accessToken || !doc.documentId) return;

    setFormData(prev => ({ ...prev, exam: doc.metadata?.exam || '' }));

    setActiveStep(1);
    await handleExtraction(doc.documentId);
  };

  const handleFileRemove = () => {
    setUploadedFile(null);
    setUploadProgress(0);
    setExtractedOECodes([]);
    setActiveStep(0);
  };

  // Handle OE code selection (supports multi-select with Ctrl/Cmd click)
  const handleOECodeSelect = (oeCode: string, isMultiSelect: boolean = false) => {
    if (isMultiSelect) {
      // Multi-select: toggle the OE code in/out of selectedOECodes
      setFormData(prev => {
        const codes = prev.selectedOECodes.includes(oeCode)
          ? prev.selectedOECodes.filter(c => c !== oeCode)
          : [...prev.selectedOECodes, oeCode];
        return {
          ...prev,
          selectedOECodes: codes,
          selectedOECode: codes.length > 0 ? codes[0] : '' // Keep first selected as primary
        };
      });
    } else {
      // Single select: replace all with this one
      setFormData(prev => ({
        ...prev,
        selectedOECode: oeCode,
        selectedOECodes: [oeCode]
      }));
    }
  };

  // Select all OE codes
  const handleSelectAllOECodes = () => {
    setFormData(prev => ({
      ...prev,
      selectedOECodes: extractedOECodes,
      selectedOECode: extractedOECodes[0] || ''
    }));
    showToast('success', 'Selected All', `All ${extractedOECodes.length} OE codes selected for generation`);
  };

  // Clear all OE code selections
  const handleClearOECodeSelection = () => {
    setFormData(prev => ({
      ...prev,
      selectedOECodes: [],
      selectedOECode: ''
    }));
  };

  // Handle topic selection - automatically updates OE codes
  const handleTopicToggle = (topicName: string) => {
    const newSelectedTopics = selectedTopics.includes(topicName)
      ? selectedTopics.filter(t => t !== topicName)
      : [...selectedTopics, topicName];

    setSelectedTopics(newSelectedTopics);

    // Automatically update OE codes based on selected topics
    if (newSelectedTopics.length === 0) {
      // No topics selected - clear OE codes
      setFormData(prev => ({
        ...prev,
        selectedOECodes: [],
        selectedOECode: ''
      }));
    } else {
      // Get OE codes for all selected topics
      const oECodesForSelectedTopics = extractedTopics
        .filter(topic => newSelectedTopics.includes(topic.name))
        .flatMap(topic => topic.relatedOECodes || [])
        .filter((code, index, self) => code && self.indexOf(code) === index);

      if (oECodesForSelectedTopics.length > 0) {
        setFormData(prev => ({
          ...prev,
          selectedOECodes: oECodesForSelectedTopics,
          selectedOECode: oECodesForSelectedTopics[0]
        }));
      }
    }
  };

  // Select all topics
  const handleSelectAllTopics = () => {
    const allTopicNames = extractedTopics.map(t => t.name);
    setSelectedTopics(allTopicNames);

    // Get OE codes for all topics
    const allOECodes = extractedTopics
      .flatMap(topic => topic.relatedOECodes || [])
      .filter((code, index, self) => code && self.indexOf(code) === index);

    if (allOECodes.length > 0) {
      setFormData(prev => ({
        ...prev,
        selectedOECodes: allOECodes,
        selectedOECode: allOECodes[0]
      }));
      showToast('success', 'All Topics Selected', `Selected ${allTopicNames.length} topics with ${allOECodes.length} OE codes`);
    }
  };

  // Clear all topic selections
  const handleClearAllTopics = () => {
    setSelectedTopics([]);
    setFormData(prev => ({
      ...prev,
      selectedOECodes: [],
      selectedOECode: ''
    }));
  };

  // Handle marking OE codes as ANBIMA or Non-ANBIMA
  const handleMarkAsANBIMA = () => {
    if (!formData.selectedOECode) {
      showToast('warning', 'No Selection', 'Please select an OE code first');
      return;
    }
    setOeCodeCategories(prev => ({
      ...prev,
      [formData.selectedOECode]: 'anbima'
    }));
    showToast('success', 'Marked as ANBIMA', `${formData.selectedOECode} marked as ANBIMA`);
  };

  const handleMarkAsNonANBIMA = () => {
    if (!formData.selectedOECode) {
      showToast('warning', 'No Selection', 'Please select an OE code first');
      return;
    }
    setOeCodeCategories(prev => ({
      ...prev,
      [formData.selectedOECode]: 'non-anbima'
    }));
    showToast('success', 'Marked as Non-ANBIMA', `${formData.selectedOECode} marked as Non-ANBIMA`);
  };

  const generateMutation = useMutation({
    mutationFn: async () => {
      // Use multiple OE codes if selected, otherwise fall back to single
      const oeCodesToUse = formData.selectedOECodes.length > 0
        ? formData.selectedOECodes
        : [formData.selectedOECode];

      const requestData = {
        exam: formData.exam,
        course: formData.course || undefined,
        oeCode: oeCodesToUse[0], // Primary OE code (for backward compatibility)
        oeCodes: oeCodesToUse, // All selected OE codes (new multi-select support)
        topics: selectedTopics.length > 0 ? selectedTopics : undefined, // Selected topics
        count: calculateQuestionCount(formData.percentage),
        questionType: formData.questionTypes[0] || 'multiple_choice',
        difficulty: formData.difficulty,
        format: formData.questionTypes.includes('multiple_choice') ? formData.mcqFormat : undefined,
        customPrompt: useCustomPrompt && customPrompt.trim() ? customPrompt.trim() : undefined
      };

      console.log('[API Request] Payload:', {
        oeCodesToUse,
        selectedOECodes: formData.selectedOECodes,
        selectedTopics,
        requestData
      });

      if (uploadedFile) {
        return await questionsService.generateFromPDF(uploadedFile, requestData);
      } else {
        return await questionsService.generateQuestions(requestData);
      }
    },
    onSuccess: (response) => {
      setActiveStep(3);
      showToast('success', 'Success!', `Generated ${response.data?.totalGenerated || 0} questions`);
    },
    onError: (error: any) => {
      showToast('error', 'Generation Failed', error.message || 'Failed to generate questions');
    }
  });

  const selectedDifficulty = DIFFICULTIES.find(d => d.value === formData.difficulty);

  return (
    <div className="min-h-screen bg-[#f5f7fa] py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/questions')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Bank
          </button>
          <div className="flex items-center space-x-3">
            <Sparkles className="w-10 h-10 text-[#1e73be]" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900">AI Question Generator</h1>
              <p className="text-gray-600 mt-1">
                Upload your document, extract OE codes, and generate high-quality questions
              </p>
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-all ${
                    index < activeStep
                      ? 'bg-[#1e73be] text-white'
                      : index === activeStep
                      ? 'bg-[#1e73be] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {index < activeStep ? <CheckCircle className="w-6 h-6" /> : index + 1}
                  </div>
                  <span className={`text-sm mt-2 text-center ${
                    index <= activeStep ? 'text-[#1e73be] font-semibold' : 'text-gray-600'
                  }`}>
                    {step}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`h-1 flex-1 transition-all ${index < activeStep ? 'bg-[#1e73be]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content - 58% */}
          <div className="lg:w-[58%]">
            {/* Step 1: Upload */}
            {activeStep === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-8">
                <div className="flex items-center space-x-2 mb-6">
                  <Upload className="w-8 h-8 text-[#1e73be]" />
                  <h2 className="text-2xl font-semibold">Step 1: Upload Document</h2>
                </div>

                <p className="text-gray-600 mb-6">
                  Upload any file containing study material. We'll extract the content and identify OE codes automatically.
                </p>

                {/* Drag & Drop */}
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
                    uploadedFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-[#1e73be] hover:bg-gray-50'
                  }`}
                >
                  {uploadedFile ? (
                    <div>
                      <FileText className="w-16 h-16 text-green-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">{uploadedFile.name}</h3>
                      <p className="text-gray-600 mb-4">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        onClick={handleFileRemove}
                        className="inline-flex items-center px-4 py-2 border-2 border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg mb-2">
                        <label className="text-[#1e73be] font-semibold cursor-pointer hover:text-[#5568d3]">
                          Choose a file
                          <input
                            type="file"
                            hidden
                            onChange={handleFileSelect}
                            accept=".pdf,.xls,.xlsx"
                          />
                        </label>
                        {' '}or drag and drop here
                      </h3>
                      <p className="text-gray-500 text-sm">
                        Supported formats: PDF, Excel (.xls, .xlsx) • Max size: 50MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Progress */}
                {uploading && (
                  <div className="mt-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-semibold">Uploading document...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${uploadProgress}%`,
                          background: 'linear-gradient(90deg, #1e73be 0%, #ea7500 100%)'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                <button
                  onClick={handleFileUpload}
                  disabled={!uploadedFile || uploading}
                  className="w-full mt-6 py-3 px-4 rounded-lg text-white font-semibold flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: uploadedFile && !uploading
                      ? 'linear-gradient(135deg, #1e73be 0%, #ea7500 100%)'
                      : '#e0e0e0'
                  }}
                >
                  <Upload className="w-5 h-5 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload & Extract Content'}
                </button>

                {/* Existing Documents */}
                {!loadingDocuments && existingDocuments.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold mb-2">Or Use Existing Document</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Select from previously uploaded documents to generate questions
                    </p>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Title</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Exam</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Chunks</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {existingDocuments.slice(0, 5).map((doc) => (
                            <tr key={doc.documentId} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-sm max-w-xs truncate">
                                {doc.metadata?.title || doc.filename}
                              </td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded border border-blue-300">
                                  {doc.metadata?.exam || 'N/A'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right">{doc.chunkCount || 0}</td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleUseExisting(doc)}
                                  className="px-4 py-1.5 rounded text-sm font-semibold text-white transition-all"
                                  style={{
                                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                                  }}
                                >
                                  Use This
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Extract */}
            {activeStep === 1 && (
              <div className="bg-white rounded-lg shadow-sm p-8">
                <div className="flex items-center space-x-2 mb-6">
                  <Search className="w-8 h-8 text-[#1e73be]" />
                  <h2 className="text-2xl font-semibold">Step 2: Extracting Content</h2>
                </div>

                <div
                  className="rounded-lg p-8 text-center border-2 border-[#1e73be]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(30, 115, 190, 0.08) 0%, rgba(234, 117, 0, 0.08) 100%)'
                  }}
                >
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#1e73be] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-[#1e73be] mb-2">AI is Analyzing Your File</h3>
                  <h4 className="text-xl font-semibold mb-3">Please Wait 2-3 Minutes</h4>
                  <p className="text-gray-700 mb-6 max-w-lg mx-auto">
                    Our AI is carefully analyzing your document and extracting relevant topics and OE codes. This process may take a few moments.
                  </p>
                  <div className="flex flex-col items-center space-y-2">
                    <span className="inline-flex items-center px-6 py-3 bg-[#1e73be] text-white rounded-full text-sm font-semibold">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Extracting Topics
                    </span>
                    <span className="inline-flex items-center px-6 py-3 bg-#ea7500 text-white rounded-full text-sm font-semibold">
                      <Search className="w-4 h-4 mr-2" />
                      Identifying OE Codes
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Configure */}
            {activeStep === 2 && (
              <div className="bg-white rounded-lg shadow-sm p-8">
                <div className="flex items-center space-x-2 mb-6">
                  <Sparkles className="w-8 h-8 text-[#1e73be]" />
                  <h2 className="text-2xl font-semibold">Step 3: Configure Settings</h2>
                </div>

                <div className="space-y-6">
                  {/* Content Topics from Uploaded Document */}
                  {extractedTopics.length > 0 && (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700">
                            Content Topics from Uploaded Document ({extractedTopics.length} found)
                          </label>
                          <p className="text-xs text-gray-500">
                            Select topics • {selectedTopics.length} selected • OE codes auto-selected
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSelectAllTopics}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all bg-blue-500 text-white hover:bg-blue-600"
                          >
                            <span>✓</span>
                            <span>Select All Topics</span>
                          </button>
                          <button
                            onClick={handleClearAllTopics}
                            disabled={selectedTopics.length === 0}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gray-500 text-white hover:bg-gray-600"
                          >
                            <span>✗</span>
                            <span>Clear All Topics</span>
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {extractedTopics.map(topic => (
                          <button
                            key={topic.name}
                            onClick={() => handleTopicToggle(topic.name)}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all relative ${
                              selectedTopics.includes(topic.name)
                                ? 'bg-[#1e73be] text-white ring-2 ring-[#1e73be] ring-offset-2'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                            title={`OE codes: ${topic.relatedOECodes?.join(', ') || 'None'}`}
                          >
                            <span>{topic.name}</span>
                            {selectedTopics.includes(topic.name) && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-white text-[#1e73be]">
                                ✓
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        💡 Click a topic to toggle selection • OE codes auto-updated based on selected topics
                      </p>
                      <hr className="border-gray-200 mt-4" />
                    </div>
                  )}

                  {/* OE Codes */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700">
                          Extracted OE Codes ({extractedOECodes.length} found)
                        </label>
                        <p className="text-xs text-gray-500">
                          Select one or multiple OE codes • {formData.selectedOECodes.length} selected
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSelectAllOECodes}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all bg-blue-500 text-white hover:bg-blue-600"
                        >
                          <span>✓</span>
                          <span>Select All</span>
                        </button>
                        <button
                          onClick={handleClearOECodeSelection}
                          disabled={formData.selectedOECodes.length === 0}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gray-500 text-white hover:bg-gray-600"
                        >
                          <span>✗</span>
                          <span>Clear All</span>
                        </button>
                        <button
                          onClick={handleMarkAsANBIMA}
                          disabled={!formData.selectedOECode}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-green-500 text-white hover:bg-green-600"
                        >
                          <span>✓</span>
                          <span>Mark ANBIMA</span>
                        </button>
                        <button
                          onClick={handleMarkAsNonANBIMA}
                          disabled={!formData.selectedOECode}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-red-500 text-white hover:bg-red-600"
                        >
                          <span>✗</span>
                          <span>Mark Non-ANBIMA</span>
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {extractedOECodes.map(oeCode => {
                        const category = oeCodeCategories[oeCode];
                        const isSelected = formData.selectedOECodes.includes(oeCode);
                        return (
                          <button
                            key={oeCode}
                            onClick={(e) => handleOECodeSelect(oeCode, e.ctrlKey || e.metaKey)}
                            title={`Click to select single | Ctrl+Click to multi-select`}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all relative ${
                              isSelected
                                ? 'bg-[#1e73be] text-white ring-2 ring-[#1e73be] ring-offset-2'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            <span>{oeCode}</span>
                            {isSelected && formData.selectedOECodes.length > 1 && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-white text-[#1e73be]">
                                ✓
                              </span>
                            )}
                            {category === 'anbima' && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-green-500 text-white">
                                ✓
                              </span>
                            )}
                            {category === 'non-anbima' && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-red-500 text-white">
                                ✗
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-600 mt-2">💡 Single-click to select one • Ctrl+Click (Cmd+Click on Mac) to select multiple</p>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Section 1: Basic Information */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">📋 Basic Information</h3>

                    {/* Exam */}
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Exam</label>
                      <select
                        value={formData.exam}
                        onChange={(e) => setFormData({ ...formData, exam: e.target.value, course: '' })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#1e73be]"
                      >
                        <option value="">Select Exam</option>
                        {exams.map(exam => (
                          <option key={exam.exam_id} value={exam.exam_id}>{exam.exam_id} - {exam.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Course */}
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Course (Optional)</label>
                      <select
                        value={formData.course}
                        onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#1e73be]"
                        disabled={filteredCourses.length === 0}
                      >
                        <option value="">All Courses</option>
                        {filteredCourses.map(course => (
                          <option key={course.course_id} value={course.course_id}>
                            {course.name}
                          </option>
                        ))}
                      </select>
                      {filteredCourses.length === 0 && formData.exam && (
                        <p className="text-xs text-gray-500 mt-1">No courses linked to this exam</p>
                      )}
                    </div>

                    {/* Total Questions */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Total Questions: {formData.percentage}% ({calculateQuestionCount(formData.percentage)} questions)
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        step="10"
                        value={formData.percentage}
                        onChange={(e) => setFormData({ ...formData, percentage: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #1e73be 0%, #1e73be ${formData.percentage}%, #e5e7eb ${formData.percentage}%, #e5e7eb 100%)`
                        }}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>10%</span>
                        <span>30%</span>
                        <span>50%</span>
                        <span>70%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Section 2: Content Selection (Macrotema → Tema → Subtema) */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">🎯 Content Selection (Hierarchical)</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Filter questions by specific knowledge area (optional)
                    </p>

                    {/* Macrotema */}
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Macrotema</label>
                      <select
                        value={formData.macrotema}
                        onChange={(e) => setFormData({ ...formData, macrotema: e.target.value, tema: '', subtema: '' })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#1e73be]"
                      >
                        <option value="">Select Macrotema (Optional)</option>
                        <option value="sistema_financeiro">Sistema Financeiro Nacional</option>
                        <option value="economia">Economia e Finanças</option>
                        <option value="instrumentos_financeiros">Instrumentos Financeiros</option>
                        <option value="gestao_carteiras">Gestão de Carteiras</option>
                        <option value="compliance">Compliance e Ética</option>
                      </select>
                    </div>

                    {/* Tema (filtered by Macrotema) */}
                    {formData.macrotema && (
                      <div className="mb-3">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Tema</label>
                        <select
                          value={formData.tema}
                          onChange={(e) => setFormData({ ...formData, tema: e.target.value, subtema: '' })}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#1e73be]"
                        >
                          <option value="">Select Tema (Optional)</option>
                          {/* Placeholder - should be populated from database based on macrotema */}
                          <option value="tema1">Tema 1 (filtered by {formData.macrotema})</option>
                          <option value="tema2">Tema 2</option>
                          <option value="tema3">Tema 3</option>
                        </select>
                      </div>
                    )}

                    {/* Subtema (filtered by Tema) */}
                    {formData.tema && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Subtema</label>
                        <select
                          value={formData.subtema}
                          onChange={(e) => setFormData({ ...formData, subtema: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#1e73be]"
                        >
                          <option value="">Select Subtema (Optional)</option>
                          {/* Placeholder - should be populated from database based on tema */}
                          <option value="subtema1">Subtema 1 (filtered by {formData.tema})</option>
                          <option value="subtema2">Subtema 2</option>
                          <option value="subtema3">Subtema 3</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-200" />

                  {/* Section 3: Skills & Cognitive Domain (ANBIMA 2026) */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">🧠 Skills & Cognitive Domain (ANBIMA 2026)</h3>

                    {/* Cognitive Domain */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">ANBIMA Cognitive Domain</label>
                      <p className="text-xs text-gray-600 mb-2">Select cognitive complexity level (optional)</p>
                      <div className="grid grid-cols-2 gap-2">
                        {COGNITIVE_DOMAINS.map(domain => (
                          <button
                            key={domain.value}
                            onClick={() => setFormData({
                              ...formData,
                              cognitiveDomain: formData.cognitiveDomain === domain.value ? undefined : domain.value
                            })}
                            className={`px-3 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                              formData.cognitiveDomain === domain.value
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                            }`}
                          >
                            <div>{domain.label}</div>
                            <div className="text-xs font-normal">{domain.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Behavioral Skills */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Behavioral Skills</label>
                      <p className="text-xs text-gray-600 mb-2">Select multiple skills (optional)</p>
                      <div className="flex flex-wrap gap-2">
                        {BEHAVIORAL_SKILLS.map(skill => (
                          <button
                            key={skill.value}
                            onClick={() => handleBehavioralSkillToggle(skill.value)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                              formData.behavioralSkills.includes(skill.value)
                                ? 'bg-purple-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-400'
                            }`}
                          >
                            {skill.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Section 4: Question Types with Sub-Options & Distribution */}
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">📝 Question Types (ANBIMA)</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Select multiple types and define percentage distribution
                    </p>

                    {QUESTION_TYPES.map(type => (
                      <div key={type.value} className="mb-4 last:mb-0">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={formData.questionTypes.includes(type.value)}
                            onChange={(e) => handleQuestionTypeToggle(type.value, e.target.checked)}
                            className="mt-1 w-5 h-5 text-[#1e73be] rounded focus:ring-[#1e73be]"
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-sm">{type.label}</div>
                            <div className="text-xs text-gray-600">{type.description}</div>

                            {/* Distribution Slider (only if selected and not short_answer) */}
                            {formData.questionTypes.includes(type.value) && type.value !== 'short_answer' && (
                              <div className="mt-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="5"
                                    value={formData.questionTypeDistribution[type.value] || 0}
                                    onChange={(e) => handleDistributionChange(type.value, parseInt(e.target.value))}
                                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                  />
                                  <span className="w-12 text-right font-semibold text-sm">
                                    {formData.questionTypeDistribution[type.value] || 0}%
                                  </span>
                                </div>

                                {/* Type-Specific Sub-Options */}
                                {/* Type 3: Questions per Case */}
                                {type.value === 'case_study' && (
                                  <div className="mt-3 pl-4 border-l-2 border-orange-300">
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                      Questions per Case (ANBIMA Type 3)
                                    </label>
                                    <div className="flex gap-2">
                                      {[1, 2, 3].map(count => (
                                        <button
                                          key={count}
                                          onClick={() => setFormData({ ...formData, questionsPerCase: count })}
                                          className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${
                                            formData.questionsPerCase === count
                                              ? 'bg-orange-600 text-white'
                                              : 'bg-white text-gray-700 border border-gray-300 hover:border-orange-400'
                                          }`}
                                        >
                                          {count} question{count > 1 ? 's' : ''}
                                        </button>
                                      ))}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Maximum 3 linked questions per case</p>
                                  </div>
                                )}

                                {/* Type 4: Prompts per Interactive */}
                                {type.value === 'interactive' && (
                                  <div className="mt-3 pl-4 border-l-2 border-orange-300">
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                      Prompts per Interactive Question (ANBIMA Type 4)
                                    </label>
                                    <input
                                      type="range"
                                      min="3"
                                      max="10"
                                      value={formData.promptsPerInteractive}
                                      onChange={(e) => setFormData({ ...formData, promptsPerInteractive: parseInt(e.target.value) })}
                                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                      <span>3 prompts</span>
                                      <span className="font-semibold text-orange-600">{formData.promptsPerInteractive} prompts</span>
                                      <span>10 prompts</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Client-consultant dialogue (chat)</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Type 2: Command Verb (outside distribution slider for short_answer) */}
                            {formData.questionTypes.includes(type.value) && type.value === 'short_answer' && (
                              <div className="mt-2">
                                <div className="mt-3 pl-4 border-l-2 border-orange-300">
                                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                                    Command Verb (ANBIMA Type 2)
                                  </label>
                                  <select
                                    value={formData.commandVerb}
                                    onChange={(e) => setFormData({ ...formData, commandVerb: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-orange-400"
                                  >
                                    {COMMAND_VERBS.map(verb => (
                                      <option key={verb.value} value={verb.value}>{verb.label}</option>
                                    ))}
                                  </select>
                                  <p className="text-xs text-gray-500 mt-1">Determines expected response type</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Total Distribution Validation */}
                    {formData.questionTypes.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-orange-200">
                        <div className="flex justify-between items-center text-sm">
                          <span className="font-semibold">Total Distribution:</span>
                          <span className={`font-bold ${
                            calculateTotalDistribution() === 100
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}>
                            {calculateTotalDistribution()}%
                            {calculateTotalDistribution() !== 100 && ' (must sum to 100%)'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-200" />

                  {/* Section 5: Character Limits (ANBIMA "sem espaços") */}
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">📏 ANBIMA Character Limits (without spaces)</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      All limits are calculated WITHOUT SPACES (text.replace(/\s/g, '').length)
                    </p>

                    {/* Tipo 1: MCQ */}
                    <div className="mb-3 p-3 bg-white rounded border border-indigo-200">
                      <div className="font-semibold text-xs text-indigo-800 mb-2">Type 1: MCQ Contextualized</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Context + Command:</span>
                          <span className="font-semibold">750-850 chars (target: ~800)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Alternatives (total):</span>
                          <span className="font-semibold">450-550 chars (target: ~500)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Each alternative:</span>
                          <span className="font-semibold">~125 chars (100-150)</span>
                        </div>
                      </div>
                    </div>

                    {/* Tipo 2: Short Answer */}
                    <div className="mb-3 p-3 bg-white rounded border border-indigo-200">
                      <div className="font-semibold text-xs text-indigo-800 mb-2">Type 2: Short Answer</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Context + Command:</span>
                          <span className="font-semibold">1,300-1,500 chars (target: ~1,400)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Model answer:</span>
                          <span className="font-semibold">150-250 words</span>
                        </div>
                        <div className="text-xs text-gray-600 italic mt-1">⚠️ Always associated with a case</div>
                      </div>
                    </div>

                    {/* Tipo 3: Case */}
                    <div className="mb-3 p-3 bg-white rounded border border-indigo-200">
                      <div className="font-semibold text-xs text-indigo-800 mb-2">Type 3: Case (Case Study)</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Case Context:</span>
                          <span className="font-semibold">1,100-1,300 chars (target: ~1,200)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Linked questions:</span>
                          <span className="font-semibold">2-3 questions (max: 3)</span>
                        </div>
                      </div>
                    </div>

                    {/* Tipo 4: Interactive */}
                    <div className="p-3 bg-white rounded border border-indigo-200">
                      <div className="font-semibold text-xs text-indigo-800 mb-2">Type 4: Interactive (Chat)</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Initial context:</span>
                          <span className="font-semibold">350-450 chars (target: ~400)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Each prompt:</span>
                          <span className="font-semibold">150-250 chars (target: ~200)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Alternatives/prompt:</span>
                          <span className="font-semibold">~500 chars total</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Section 6: Difficulty */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nível de Dificuldade</label>
                    <div className="grid grid-cols-3 gap-2">
                      {DIFFICULTIES.map(diff => (
                        <button
                          key={diff.value}
                          onClick={() => setFormData({ ...formData, difficulty: diff.value })}
                          className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                            formData.difficulty === diff.value
                              ? 'text-white border-transparent'
                              : 'bg-white text-gray-700 border-gray-300'
                          }`}
                          style={{
                            background: formData.difficulty === diff.value ? diff.color : 'white',
                            borderColor: formData.difficulty === diff.value ? diff.color : '#d1d5db'
                          }}
                        >
                          {diff.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Section 7: ANBIMA Validation Rules */}
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-300">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">⚠️ Regras de Validação ANBIMA</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Desative validações específicas se necessário (não recomendado para produção)
                    </p>

                    <div className="space-y-2">
                      <label className="flex items-start cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={formData.validationRules.prohibitedPhrases}
                          onChange={(e) => setFormData({
                            ...formData,
                            validationRules: { ...formData.validationRules, prohibitedPhrases: e.target.checked }
                          })}
                          className="mt-0.5 w-4 h-4 text-[#1e73be] rounded focus:ring-[#1e73be]"
                        />
                        <div className="ml-2 flex-1">
                          <span className="text-sm font-semibold text-gray-900">Frases Proibidas</span>
                          <p className="text-xs text-gray-600">Valida "Todas as anteriores", "Nenhuma das anteriores", etc.</p>
                        </div>
                      </label>

                      <label className="flex items-start cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={formData.validationRules.characterLimits}
                          onChange={(e) => setFormData({
                            ...formData,
                            validationRules: { ...formData.validationRules, characterLimits: e.target.checked }
                          })}
                          className="mt-0.5 w-4 h-4 text-[#1e73be] rounded focus:ring-[#1e73be]"
                        />
                        <div className="ml-2 flex-1">
                          <span className="text-sm font-semibold text-gray-900">Limites de Caracteres</span>
                          <p className="text-xs text-gray-600">Valida limites ANBIMA "sem espaços" para todos os tipos</p>
                        </div>
                      </label>

                      <label className="flex items-start cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={formData.validationRules.structuralValidation}
                          onChange={(e) => setFormData({
                            ...formData,
                            validationRules: { ...formData.validationRules, structuralValidation: e.target.checked }
                          })}
                          className="mt-0.5 w-4 h-4 text-[#1e73be] rounded focus:ring-[#1e73be]"
                        />
                        <div className="ml-2 flex-1">
                          <span className="text-sm font-semibold text-gray-900">Validação Estrutural</span>
                          <p className="text-xs text-gray-600">Valida formato ANBIMA (contexto + comando, 4 alternativas, etc.)</p>
                        </div>
                      </label>

                      <label className="flex items-start cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={formData.validationRules.caseAssociation}
                          onChange={(e) => setFormData({
                            ...formData,
                            validationRules: { ...formData.validationRules, caseAssociation: e.target.checked }
                          })}
                          className="mt-0.5 w-4 h-4 text-[#1e73be] rounded focus:ring-[#1e73be]"
                        />
                        <div className="ml-2 flex-1">
                          <span className="text-sm font-semibold text-gray-900">Associação de Case (Tipo 2)</span>
                          <p className="text-xs text-gray-600">Tipo 2 DEVE estar sempre associado a um case (ANBIMA)</p>
                        </div>
                      </label>
                    </div>
                  </div>


                  {/* Custom Prompt Toggle */}
                  <div>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useCustomPrompt}
                        onChange={(e) => setUseCustomPrompt(e.target.checked)}
                        className="w-5 h-5 text-[#1e73be] rounded focus:ring-[#1e73be]"
                      />
                      <div>
                        <div className="flex items-center space-x-2">
                          <Edit2 className={`w-5 h-5 ${useCustomPrompt ? 'text-[#1e73be]' : 'text-gray-500'}`} />
                          <span className="text-sm font-semibold text-gray-700">Custom Prompt Mode</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-7">Write your own instructions for question generation</p>
                      </div>
                    </label>
                  </div>

                  {useCustomPrompt && (
                    <div>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        rows={6}
                        placeholder="Example: Generate 5 MCQ questions about investment risk management, focusing on market risk vs credit risk. Include practical scenarios with Brazilian financial market examples."
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#1e73be]"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Tip: Be specific about the topic, number of questions, question type, and any specific scenarios you want.
                      </p>
                    </div>
                  )}

                  {/* Generate Button */}
                  <button
                    onClick={() => generateMutation.mutate()}
                    disabled={
                      !formData.exam ||
                      (useCustomPrompt ? !customPrompt.trim() : formData.selectedOECodes.length === 0) ||
                      formData.questionTypes.length === 0 ||
                      (formData.questionTypes.length > 1 && calculateTotalDistribution() !== 100) ||
                      generateMutation.isPending
                    }
                    className="w-full py-3 px-4 rounded-lg text-white font-semibold flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: (
                        !formData.exam ||
                        (useCustomPrompt ? !customPrompt.trim() : formData.selectedOECodes.length === 0) ||
                        formData.questionTypes.length === 0 ||
                        (formData.questionTypes.length > 1 && calculateTotalDistribution() !== 100)
                      )
                        ? '#e0e0e0'
                        : 'linear-gradient(135deg, #1e73be 0%, #ea7500 100%)'
                    }}
                  >
                    {generateMutation.isPending ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Gerando Questões...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Gerar Questões
                      </>
                    )}
                  </button>

                  {/* Validation Messages */}
                  {!useCustomPrompt && formData.selectedOECodes.length === 0 && (
                    <p className="text-xs text-red-600 mt-2">⚠️ Selecione pelo menos um código OE ou use "Prompt Personalizado"</p>
                  )}
                  {formData.questionTypes.length === 0 && (
                    <p className="text-xs text-red-600 mt-2">⚠️ Selecione pelo menos um tipo de questão</p>
                  )}
                  {formData.questionTypes.length > 1 && calculateTotalDistribution() !== 100 && (
                    <p className="text-xs text-red-600 mt-2">
                      ⚠️ A distribuição dos tipos de questão deve somar 100% (atual: {calculateTotalDistribution()}%)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Results */}
            {activeStep === 3 && (
              <div className="bg-white rounded-lg shadow-sm p-8">
                {generateMutation.isPending && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#1e73be] mx-auto mb-4" />
                    <h3 className="text-2xl font-semibold mb-2">Generating Questions...</h3>
                    <p className="text-gray-600">Our AI is crafting high-quality questions based on your document</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-6">
                      <div className="h-2 bg-[#1e73be] rounded-full animate-pulse" style={{ width: '70%' }} />
                    </div>
                  </div>
                )}

                {generateMutation.isSuccess && (
                  <div>
                    <div className="flex items-center space-x-3 mb-6">
                      <CheckCircle className="w-12 h-12 text-green-600" />
                      <h2 className="text-3xl font-bold text-green-600">Successfully Generated!</h2>
                    </div>

                    <div className="bg-green-50 rounded-lg p-8 text-center mb-6">
                      <div className="text-6xl font-bold text-green-600 mb-2">
                        {generateMutation.data?.data?.questions?.length || 0}
                      </div>
                      <div className="text-gray-700 text-lg">Questions Created</div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-gray-100 rounded-lg p-4 text-center">
                        <TrendingUp className="w-10 h-10 text-[#1e73be] mx-auto mb-2" />
                        <div className="text-xl font-semibold">
                          {generateMutation.data?.data?.metadata?.successRate
                            ? `${(generateMutation.data.data.metadata.successRate * 100).toFixed(0)}%`
                            : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-600">Success Rate</div>
                      </div>
                      <div className="bg-gray-100 rounded-lg p-4 text-center">
                        <Clock className="w-10 h-10 text-[#1e73be] mx-auto mb-2" />
                        <div className="text-xl font-semibold">
                          {generateMutation.data?.data?.metadata?.totalTimeMs
                            ? `${(generateMutation.data.data.metadata.totalTimeMs / 1000).toFixed(1)}s`
                            : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-600">Processing Time</div>
                      </div>
                      <div className="bg-gray-100 rounded-lg p-4 text-center">
                        <DollarSign className="w-10 h-10 text-[#1e73be] mx-auto mb-2" />
                        <div className="text-xl font-semibold">
                          ${generateMutation.data?.data?.metadata?.totalCost?.toFixed(3) || '0.000'}
                        </div>
                        <div className="text-xs text-gray-600">Generation Cost</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={() => navigate('/questions')}
                        className="w-full py-3 px-4 rounded-lg text-white font-semibold"
                        style={{
                          background: 'linear-gradient(135deg, #1e73be 0%, #ea7500 100%)'
                        }}
                      >
                        View Questions in Bank
                      </button>
                      <button
                        onClick={() => {
                          setActiveStep(0);
                          setUploadedFile(null);
                          setExtractedOECodes([]);
                          generateMutation.reset();
                        }}
                        className="w-full py-3 px-4 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                      >
                        Generate More Questions
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - 42% */}
          <div className="lg:w-[42%] space-y-6">
            {/* Current Configuration */}
            <div
              className="rounded-lg p-6 text-white"
              style={{
                background: 'linear-gradient(135deg, #1e73be 0%, #ea7500 100%)'
              }}
            >
              <h3 className="text-xl font-semibold mb-4">Configuração Atual</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Documento:</span>
                  <span className="font-semibold truncate ml-2">{uploadedFile?.name || 'Não enviado'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Topics Selecionados:</span>
                  <span className="font-semibold">{selectedTopics.length}/{extractedTopics.length}</span>
                </div>
                {selectedTopics.length > 0 && (
                  <div className="text-xs">
                    {selectedTopics.map(topic => (
                      <span key={topic} className="inline-block bg-white text-[#1e73be] px-2 py-1 rounded mr-1 mb-1 max-w-xs truncate">
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex justify-between">
                  <span>OE Codes Extraídos:</span>
                  <span className="font-semibold">{extractedOECodes.length} encontrados</span>
                </div>
                <div className="flex justify-between">
                  <span>OE Codes Selecionados:</span>
                  <span className="font-semibold">{formData.selectedOECodes.length} selecionados</span>
                </div>
                {formData.selectedOECodes.length > 0 && (
                  <div className="text-xs">
                    {formData.selectedOECodes.map(code => (
                      <span key={code} className="inline-block bg-white text-[#1e73be] px-2 py-1 rounded mr-1 mb-1">
                        {code}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Exame:</span>
                  <span className="font-semibold">{formData.exam || 'Não selecionado'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Questões:</span>
                  <span className="font-semibold">
                    {formData.percentage}% ({calculateQuestionCount(formData.percentage)})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Tipos Selecionados:</span>
                  <span className="font-semibold">{formData.questionTypes.length || 0}</span>
                </div>
                {formData.questionTypes.length > 1 && (
                  <div className="flex justify-between">
                    <span>Distribuição:</span>
                    <span className={`font-semibold ${calculateTotalDistribution() === 100 ? 'text-green-300' : 'text-red-300'}`}>
                      {calculateTotalDistribution()}%
                    </span>
                  </div>
                )}
                {formData.cognitiveDomain && (
                  <div className="flex justify-between">
                    <span>Domínio Cognitivo:</span>
                    <span className="font-semibold">Nível {formData.cognitiveDomain}</span>
                  </div>
                )}
                {formData.behavioralSkills.length > 0 && (
                  <div className="flex justify-between">
                    <span>Habilidades:</span>
                    <span className="font-semibold">{formData.behavioralSkills.length} selecionadas</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span>Dificuldade:</span>
                  <span className="px-3 py-1 rounded text-xs font-semibold capitalize" style={{ backgroundColor: selectedDifficulty?.color }}>
                    {formData.difficulty}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Modo:</span>
                  <span className={`px-3 py-1 rounded text-xs font-semibold ${
                    useCustomPrompt ? 'bg-green-500' : 'bg-orange-500'
                  }`}>
                    {useCustomPrompt ? 'Prompt Personalizado' : 'Template'}
                  </span>
                </div>
                {formData.validationRules && (
                  <div className="flex justify-between">
                    <span>Validações Ativas:</span>
                    <span className="font-semibold">
                      {Object.values(formData.validationRules).filter(Boolean).length}/4
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Como Funciona</h3>
              <ol className="space-y-3 text-sm text-gray-700">
                <li className="flex">
                  <span className="font-bold mr-2">1.</span>
                  <span><strong>Upload:</strong> Envie material de estudo (PDF, Excel)</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">2.</span>
                  <span><strong>Extração:</strong> Sistema identifica automaticamente OE codes</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">3.</span>
                  <span><strong>Configuração:</strong> Selecione tipos ANBIMA, habilidades, e limites</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">4.</span>
                  <span><strong>Geração:</strong> AI cria questões 7 padrões ANBIMA 2026</span>
                </li>
              </ol>
            </div>

            {/* ANBIMA 2026 Features */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">✨ Recursos ANBIMA 2026</h3>
              <ul className="space-y-2 text-xs text-gray-700">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span><strong>4 Tipos de Questão:</strong> MCQ, Resposta Curta, Case, Interativa</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span><strong>Limites "sem espaços":</strong> Contagem de caracteres ANBIMA</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span><strong>Domínio Cognitivo:</strong> 4 níveis de complexidade</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span><strong>Habilidades Comportamentais:</strong> Atendimento, comunicação, ética</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span><strong>Validações Automáticas:</strong> Frases proibidas, estrutura, limites</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span><strong>Distribuição Flexível:</strong> Múltiplos tipos em um único batch</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
